/**
 * FundraiserDisabled.tsx - Disabled/Inactive Fundraiser Display
 *
 * This component displays fundraisers that are inactive, completed, or disabled,
 * providing appropriate messaging and limited functionality for non-active fundraisers.
 *
 * Key Features:
 * - Status-specific messaging (inactive, completed, expired)
 * - Limited functionality display (no donations accepted)
 * - Fundraiser information display with progress
 * - Back navigation to dashboard
 * - Promoter context preservation
 * - Responsive design for all device sizes
 *
 * Status Handling:
 * - Inactive: Fundraiser not yet started or paused
 * - Completed: Fundraiser reached end date or goal
 * - Expired: Fundraiser past end date
 *
 * Props:
 * - isPreview?: boolean - Whether in preview mode
 * - promoterId?: string - Promoter ID for context preservation
 *
 * Dependencies: MobX store, styled-components, Lucide icons
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ArrowLeft } from "lucide-react";
import * as Icons from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { FundRaiserStatus, IconType } from "../enums";
import { NavigateFunction } from "react-router-dom";
import { withRouter } from "@mindmemobile/mm-shared-global";

const AppContainer = styled.div<{ backgroundColor?: string }>`
  min-height: 100vh;
  background: ${(props) => props.backgroundColor || "#f8fafc"};
  position: relative;
`;

const BackToDashboard = styled.button`
  position: fixed;
  top: 24px;
  left: 24px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border: 1px solid #e2e8f0;
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #ffffff;
    border-color: #cbd5e1;
    transform: translateY(-1px);
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  position: relative;
  z-index: 1;
  min-height: calc(100vh - 200px);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DisabledContainer = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 48px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid #f1f5f9;
  text-align: center;
  max-width: 600px;
  width: 100%;
`;

const IconContainer = styled.div`
  width: 140px;
  height: 140px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 32px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: hidden;
`;

const IconWrapper = styled.div<{ iconColor: string; bgColor: string }>`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.iconColor};
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
`;

const FundraiserImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 16px;
  line-height: 1.2;
`;

const Description = styled.p`
  font-size: 18px;
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 480px;
  margin-left: auto;
  margin-right: auto;
`;

interface FundraiserDisabledProps {
  isPreview?: boolean;
  promoterId?: string;
  navigate: NavigateFunction;
}

// Helper function to determine if a color is light or dark
const isLightColor = (hexColor: string): boolean => {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

// Helper function to darken a color
const darkenColor = (hexColor: string, percent: number = 40): string => {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  const darkenedR = Math.max(0, Math.floor(r * (1 - percent / 100)));
  const darkenedG = Math.max(0, Math.floor(g * (1 - percent / 100)));
  const darkenedB = Math.max(0, Math.floor(b * (1 - percent / 100)));

  return `#${darkenedR.toString(16).padStart(2, "0")}${darkenedG.toString(16).padStart(2, "0")}${darkenedB.toString(16).padStart(2, "0")}`;
};

  const FundraiserDisabled = observer(
  ({ isPreview = false, promoterId, navigate }: FundraiserDisabledProps) => {
    const selectedFundraiser = fundraiserStore.selectedFundraiser;
    const builderData = fundraiserStore.builderData;
    const customColors = builderData.customColors;

    if (!selectedFundraiser) {
      return null;
    }

    const getFundraiserTitle = () => {
      return builderData.title || selectedFundraiser.title || "Fundraiser";
    };

    const getStatusDescription = () => {
      switch (selectedFundraiser.status) {
        case FundRaiserStatus.Completed:
          return "Thank you for your interest in supporting our fundraiser. This fundraiser has been completed and is no longer accepting new donations.";
        case FundRaiserStatus.Inactive:
          return "Thank you for your interest in supporting our fundraiser. This fundraiser is not active and cannot accept new donations.";
        default:
          return "Thank you for your interest in supporting our fundraiser. This fundraiser is not active and cannot accept new donations.";
      }
    };

    const renderFundraiserVisual = () => {
      const iconImageChoice = builderData.iconImageChoice || IconType.None;

      // Use builderData for logo, not selectedFundraiser media
      const fundraiserImage = builderData?.fundraiserImage;

      if (iconImageChoice === IconType.Image && fundraiserImage) {
        return (
          <FundraiserImage
            src={fundraiserImage.fullURL}
            alt={getFundraiserTitle()}
          />
        );
      } else if (iconImageChoice === IconType.Icon) {
        const iconName = builderData.titleIcon || "Heart";
        const iconColor = builderData.titleColor?.hex || "#10b981";
        const Icon = (Icons as any)[iconName] || Icons.Heart;

        // Get icon colors based on background
        const bgColor = iconColor;
        const foregroundColor = isLightColor(iconColor)
          ? darkenColor(iconColor)
          : "#ffffff";

        return (
          <IconWrapper iconColor={foregroundColor} bgColor={bgColor}>
            <Icon size={36} />
          </IconWrapper>
        );
      } else {
        // Default fallback icon
        return (
          <IconWrapper iconColor="#ffffff" bgColor="#94a3b8">
            <Icons.Heart size={36} />
          </IconWrapper>
        );
      }
    };

    return (
      <AppContainer backgroundColor={customColors.background.hex}>
        {!isPreview && (
          <BackToDashboard onClick={() => navigate("/fundraiser/dashboard")}>
            <ArrowLeft size={20} />
          </BackToDashboard>
        )}

        <ContentWrapper>
          <DisabledContainer>
            <IconContainer>{renderFundraiserVisual()}</IconContainer>

            <Title>{getFundraiserTitle()}</Title>

            <Description>{getStatusDescription()}</Description>
          </DisabledContainer>
        </ContentWrapper>
      </AppContainer>
    );
  }
);


export default withRouter(FundraiserDisabled);